import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { gsap } from 'gsap';
import { CAMERA_POSITIONS } from './constants';

export default class CameraManager {
  constructor(container, renderer) {
    this.container = container;
    this.renderer = renderer;
    this.setupCamera();
    this.setupControls();
    this.mousePanTarget = new THREE.Vector3();
    this.currentSection = 'home';
  }

  setupCamera() {
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;
    
    this.camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
    this.camera.position.copy(CAMERA_POSITIONS.home.position);
    this.camera.lookAt(CAMERA_POSITIONS.home.target);
    
    // Set up a target vector for the camera
    this.cameraTarget = CAMERA_POSITIONS.home.target.clone();
  }

  setupControls() {
    // Add orbit controls for debugging
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.target.copy(this.cameraTarget);
    this.controls.update();
    this.controls.enabled = false; // Disable for the final version
  }

  handleMousePan(mouseX, mouseY) {
    if (!this.camera || !CAMERA_POSITIONS[this.currentSection]) return;

    const basePosition = CAMERA_POSITIONS[this.currentSection].position.clone();
    const panStrength = {
      home: { x: 0.2, y: 0.1 },
      work: { x: 0.3, y: 0.15 },
      about: { x: 0.3, y: 0.15 }
    }[this.currentSection] || { x: 0.2, y: 0.1 };
    
    // Calculate pan offset based on section-specific constraints
    this.mousePanTarget.copy(basePosition);
    this.mousePanTarget.x += mouseX * panStrength.x;
    this.mousePanTarget.y += mouseY * panStrength.y;

    // Add section-specific constraints
    switch (this.currentSection) {
      case 'home':
        // Closer view, smaller pan range
        this.mousePanTarget.x = THREE.MathUtils.clamp(this.mousePanTarget.x, basePosition.x - 0.3, basePosition.x + 0.3);
        this.mousePanTarget.y = THREE.MathUtils.clamp(this.mousePanTarget.y, basePosition.y - 0.2, basePosition.y + 0.2);
        break;
      case 'work':
        // Medium distance, medium pan range
        this.mousePanTarget.x = THREE.MathUtils.clamp(this.mousePanTarget.x, basePosition.x - 0.5, basePosition.x + 0.5);
        this.mousePanTarget.y = THREE.MathUtils.clamp(this.mousePanTarget.y, basePosition.y - 0.3, basePosition.y + 0.3);
        break;
      case 'about':
        // Further view, larger pan range
        this.mousePanTarget.x = THREE.MathUtils.clamp(this.mousePanTarget.x, basePosition.x - 0.6, basePosition.x + 0.6);
        this.mousePanTarget.y = THREE.MathUtils.clamp(this.mousePanTarget.y, basePosition.y - 0.4, basePosition.y + 0.4);
        break;
    }

    // Smoothly move camera to new position
    gsap.to(this.camera.position, {
      x: this.mousePanTarget.x,
      y: this.mousePanTarget.y,
      duration: 1,
      ease: "power2.out",
      onUpdate: () => {
        // Ensure camera keeps looking at the target
        this.camera.lookAt(this.cameraTarget);
      }
    });
  }

  transitionToSection(section) {
    this.currentSection = section;
    if (!CAMERA_POSITIONS[section]) {
      console.error(`Invalid section: ${section}`);
      return;
    }
    
    console.log(`Transitioning to ${section}`);
    
    const endPosition = CAMERA_POSITIONS[section].position.clone();
    const endTarget = CAMERA_POSITIONS[section].target.clone();
    
    // Update camera target
    this.cameraTarget = endTarget.clone();
    
    // Use GSAP for smooth camera transitions
    const duration = 1.5;
    
    // Animate camera position
    gsap.to(this.camera.position, {
      x: endPosition.x,
      y: endPosition.y,
      z: endPosition.z,
      duration,
      ease: "power2.inOut",
      onUpdate: () => {
        // Make sure camera looks at the target during transition
        this.camera.lookAt(this.cameraTarget);
        
        // Update controls target as well
        if (this.controls) {
          this.controls.target.copy(this.cameraTarget);
          this.controls.update();
        }
      }
    });
    
    // Animate camera target
    gsap.to(this.cameraTarget, {
      x: endTarget.x,
      y: endTarget.y,
      z: endTarget.z,
      duration,
      ease: "power2.inOut",
      onComplete: () => {
        // Ensure camera is properly looking at the target
        this.camera.lookAt(this.cameraTarget);
        
        // Update controls
        if (this.controls) {
          this.controls.target.copy(this.cameraTarget);
          this.controls.update();
        }
      }
    });
  }

  onResize() {
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;
    
    // Update camera aspect ratio
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  getCamera() {
    return this.camera;
  }

  update() {
    if (this.controls && this.controls.enabled) {
      this.controls.update();
    }
    
    if (this.camera && this.cameraTarget) {
      this.camera.lookAt(this.cameraTarget);
    }
  }
} 